import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  private readonly handleDialogCloseBound = this.handleDialogClose.bind(this)
  private readonly preserveFilesBound = this.preserveFiles.bind(this)

  connect (): void {
    this.dialog?.addEventListener('close', this.handleDialogCloseBound)
    document.addEventListener('turbo:before-stream-render', this.preserveFilesBound)
  }

  disconnect (): void {
    this.dialog?.removeEventListener('close', this.handleDialogCloseBound)
    document.removeEventListener('turbo:before-stream-render', this.preserveFilesBound)
  }

  handleSubmit (event: CustomEvent): void {
    const { detail } = event as CustomEvent<{ success: boolean }>

    if (detail?.success) {
      this.closeSlideover()
    }
  }

  handleCancel (): void {
    this.resetForm()
    this.closeSlideover()
  }

  private handleDialogClose (): void {
    this.resetForm()
    this.dispatchReopen()
  }

  private resetForm (): void {
    const form = this.element.querySelector<HTMLFormElement>('form')
    if (form === null) return

    form.reset()
    const nameInput = form.querySelector<HTMLInputElement>('[name="evidence[name]"]')
    if (nameInput !== null) {
      nameInput.value = ''
      nameInput.defaultValue = ''
    }

    form.querySelectorAll('.is-invalid, .was-invalid').forEach(field => field.classList.remove('is-invalid', 'was-invalid'))
    form.querySelectorAll('.error-message').forEach(error => error.remove())

    const dateInput = form.querySelector<HTMLInputElement & { _flatpickrInstance?: { clear: (triggerChange: boolean) => void } }>('[name="evidence[captured_at]"]')
    if (dateInput != null) {
      if (dateInput._flatpickrInstance != null) dateInput._flatpickrInstance.clear(false)
      else dateInput.value = ''
    }

    const filesElement = form.querySelector<HTMLElement>('[data-controller~="file-removable"]')
    if (filesElement === null) return

    const filesController = this.application.getControllerForElementAndIdentifier(filesElement, 'file-removable') as { clear: () => void } | null
    filesController?.clear()
    const fileInput = filesElement.querySelector<HTMLInputElement>('input[type="file"][name="evidence[new_files][]"]')
    if (fileInput !== null) fileInput.value = ''
    filesElement.querySelector('[data-file-removable-target="list"]')?.replaceChildren()
  }

  private preserveFiles (event: Event): void {
    const form = this.element.querySelector<HTMLFormElement>('form')
    const stream = event.target as HTMLElement
    if (form === null || stream.getAttribute('action') !== 'replace' || stream.getAttribute('target') !== form.id) return

    const input = form.querySelector<HTMLInputElement>('input[type="file"][name="evidence[new_files][]"]')
    const files = Array.from(input?.files ?? [])
    if (files.length === 0) return

    const detail = (event as CustomEvent<{ render: (stream: HTMLElement) => Promise<void> }>).detail
    const render = detail.render
    detail.render = async (streamElement: HTMLElement): Promise<void> => {
      await render(streamElement)
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
      if (this.dialog?.open !== true) return

      const newInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="evidence[new_files][]"]')
      if (newInput === null) return

      const transfer = new DataTransfer()
      files.forEach(file => transfer.items.add(file))
      const filesElement = newInput.closest<HTMLElement>('[data-controller~="file-removable"]')
      if (filesElement === null) return

      const filesController = this.application.getControllerForElementAndIdentifier(filesElement, 'file-removable') as {
        selectMultipleFiles: (event: { dataTransfer: DataTransfer, target: HTMLInputElement }) => void
      } | null
      if (filesController !== null) filesController.selectMultipleFiles({ dataTransfer: transfer, target: newInput })
    }
  }

  private closeSlideover (): void {
    const controller = this.getSlideoverController()

    if (controller !== null && typeof (controller as { close?: () => void }).close === 'function') {
      (controller as { close: () => void }).close()
      return
    }

    this.dialog?.close()
  }

  private dispatchReopen (): void {
    document.dispatchEvent(new CustomEvent('respond-slideover:reopen', { bubbles: true }))
  }

  private get slideoverElement (): HTMLElement | null {
    return this.element.closest('[data-controller~="slideover"]')
  }

  private get dialog (): HTMLDialogElement | null {
    return this.slideoverElement?.querySelector('[data-slideover-target="dialog"]') as HTMLDialogElement | null
  }

  private getSlideoverController (): any {
    const element = this.slideoverElement
    if (element == null) return null

    const application: any = this.application
    return application?.getControllerForElementAndIdentifier?.(element, 'slideover') ?? null
  }
}
